import type { FilePublic } from '@repo/types/files';
import type { ComponentProps } from 'react';

import { cn } from '@repo/lib/cn';
import { FileTypeUtils } from '@repo/lib/fs/fileTypeUtils';
import { getBase64 } from '@repo/lib/fs/getBase64';
import { prettyBytes } from '@repo/lib/prettyBytes';
import { FileType } from '@repo/types/enums/fileType';
import { AudioLinesIcon, FileUp, VideoIcon, XIcon } from 'lucide-react';
import { useMemo } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Image } from '@/components/ui/image';
import { AudioPlayer, PlayStopButton, VideoPlayer, VideoPlayerOverlay } from '@/components/ui/media-player';
import { getFileUrl } from '@/utils/file';

export interface FileUploadCardProps extends ComponentProps<'div'> {
  file: File | FilePublic;
  onRemove: () => void;
}

export function FileInputCard(fileUploadCardProps: FileUploadCardProps) {
  const { className, file, onRemove, ...props } = fileUploadCardProps;

  const remove = (e: React.MouseEvent<HTMLButtonElement>) => {
    onRemove();
    e.preventDefault();
  };

  return (
    <div className={cn('relative flex aspect-square gap-2', className)} {...props}>
      <div className="border-border-primary relative h-full w-full overflow-hidden rounded-md border">
        <FileInputCardContent file={file} />
      </div>
      <Button
        type="button"
        onClick={remove}
        variant={'tertiary'}
        size="sm"
        className="absolute -top-1.5 -right-1.5 z-10 h-6 min-h-6 w-6 min-w-6 rounded-full border-2 shadow-none"
        aria-label="Remove item"
      >
        <XIcon className="size-3" />
      </Button>
    </div>
  );
}

export interface FileUploadCardContentProps {
  className?: string;
  file: File | FilePublic;
}

function FileInputCardContent(fileUploadCardContentProps: FileUploadCardContentProps) {
  const { file, className, ...props } = fileUploadCardContentProps;
  const isFileInstance = file instanceof File;

  const { fileType, type } = useMemo(() => {
    const type = isFileInstance ? file.type : file.fileType;
    return {
      fileType: FileTypeUtils.getServeFileType(type, {
        pick: [FileType.IMAGE, FileType.VIDEO, FileType.AUDIO],
      }),
      type: type,
    };
  }, [file, isFileInstance]);

  const objectURL = useMemo<string>(() => {
    if (isFileInstance) {
      return URL.createObjectURL(file);
    }
    return '';
  }, []);

  const fileURL = useMemo<string>(() => {
    if (isFileInstance) {
      return objectURL;
    } else if (file.id) {
      return getFileUrl(file.id);
    }
    return '';
  }, [isFileInstance, file, objectURL]);

  const base64 = getBase64({
    base64: !isFileInstance ? (file.thumbnail ?? undefined) : undefined,
    fileType: type,
    clear: true,
  });

  switch (fileType) {
    case FileType.IMAGE:
      return <Image src={fileURL} alt={file.name} placeholder={'blur'} blurDataURL={!isFileInstance ? base64 : undefined} fill />;
    case FileType.VIDEO:
      return (
        <VideoPlayer src={fileURL} title={'Video'} className={'z-10 aspect-square h-full w-full object-cover'}>
          <Badge variant={'muted'} border size={'sm'} className="absolute top-1 left-1">
            <VideoIcon /> Video
          </Badge>
          <VideoPlayerOverlay className={'size-11 rounded-md'} />
        </VideoPlayer>
      );
    case FileType.AUDIO:
      return (
        <AudioPlayer src={fileURL} className={'relative z-10 h-full w-full'}>
          <Badge variant={'muted'} border size={'sm'} className="absolute top-1 left-1">
            <AudioLinesIcon /> Audio
          </Badge>
          <PlayStopButton className={'mx-auto size-11 rounded-md'} />
        </AudioPlayer>
      );
    default:
      return (
        <div className={cn('flex h-full w-full flex-col items-center justify-center select-none', className)} {...props}>
          <FileUp className={'text-muted-foreground size-5'} />
          <p className="text-muted-foreground mt-3 w-full truncate px-1 text-center text-xs font-medium">{file.name}</p>
          <span className="text-muted-foreground mt-1 w-full px-1 text-center text-xs font-medium">{isFileInstance && prettyBytes(file.size)}</span>
        </div>
      );
  }
}
